/**
 * A label that appears beside its trigger on hover and on keyboard focus.
 *
 * Rendered into `document.body` at a fixed position rather than absolutely
 * inside the trigger: the sidebar's nav scrolls, and an absolute tooltip inside
 * an `overflow` container is clipped at its edge.
 *
 * The tooltip is visual only (`aria-hidden`). The trigger must carry its own
 * accessible name — an `aria-label` on an icon-only button — so a screen reader
 * does not announce the label twice.
 */

import { useCallback, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface TooltipProps {
    label: string
    /** When false the children render alone, e.g. while the sidebar is expanded. */
    enabled?: boolean
    side?: 'right' | 'bottom'
    className?: string
    children: ReactNode
}

interface Position {
    top: number
    left: number
}

const GAP = 8

export default function Tooltip({ label, enabled = true, side = 'right', className, children }: TooltipProps) {
    const anchorRef = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<Position | null>(null)

    const show = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect()
        if (!rect) return
        setPosition(
            side === 'right'
                ? { top: rect.top + rect.height / 2, left: rect.right + GAP }
                : { top: rect.bottom + GAP, left: rect.left + rect.width / 2 },
        )
    }, [side])
    const hide = useCallback(() => setPosition(null), [])

    if (!enabled) return <>{children}</>

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a wrapper: focus and hover come from the trigger inside it
        <div
            ref={anchorRef}
            className={className}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            onKeyDown={(event) => event.key === 'Escape' && hide()}
        >
            {children}
            {position &&
                createPortal(
                    <div
                        aria-hidden="true"
                        style={{ top: position.top, left: position.left }}
                        className={`pointer-events-none fixed z-100 whitespace-nowrap rounded-lg bg-navy px-2.5 py-1.5 text-xs font-semibold text-white shadow-raised ring-1 ring-white/10 dark:bg-gray-700 ${
                            side === 'right' ? '-translate-y-1/2' : '-translate-x-1/2'
                        }`}
                    >
                        {label}
                    </div>,
                    document.body,
                )}
        </div>
    )
}
